/* Built In Imports */
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box, Heading } from '@chakra-ui/react';
import filter from 'lodash/filter';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import ProgressiveImage from '@components/UI/ProgressiveImage';
import HeadComponent from '@components/Utility/HeadComponent';
import { setAudioPlayer } from '@components/Utility/utils';
import config from '@config';
import MidContainer from '@Layout/MidContainer';
import PageConfig from '@msr/Common/PageConfig';
import RelatedContent from '@msr/components/Card/RelatedContent';
import ShivaTopic from '@msr/components/Card/ShivaTopic';
import MediaEmbed from '@msr/components/Embeds/MediaEmbed';
import WrapLayout from '@msr/components/Layout/WrapLayout';
import Breadcrum from '@msr/components/Navigation/Breadcrum';
import MsrImageSlider from '@msr/components/Tables/MsrImagerSlider';
import SubscribeForm from '@msr/components/UI/Blogs/SubscribeForm';
import StructuredTextContent from '@msr/components/UI/StructuredTextContent';
import SocialIconMob from '@msr/components/Utility/SocialIconMob';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
  fetchFooterDetailData,
  fetchHeaderMenuData
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/msrService';

/**
 *
 * @param pageData.pageData
 * @param {object} pageData
 * @returns
 */
const ShivaBlog = ({ pageData }) => {
  const authContext = useContext(AuthContext);
  const router = useRouter();
  const [isAudioLoad, setAudioLoad] = useState(false);
  const imagegallery = pageData?.imageGallery;

  const embedObj = {
    embedUrl: pageData?.videoUrl,
    source: 'youtube',
  };

  useEffect(() => {
    authContext.RegionAndLang('', 'en');
    if (typeof window !== 'undefined' && pageData?.types == 'audio' && isAudioLoad) {
      setAudioPlayer(pageData?.audioUrl);
    }
  }, [authContext, '', 'en', isAudioLoad]);

  let topicsUnAvailable = filter(pageData?.topic, function (topic) {
    return topic?.title === '';
  });

  const handleAudioClick = () => {
    if (!isAudioLoad) {
      setAudioLoad(true);
    }
  };

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      />
      <Breadcrum
        page={pageData.breadcrumbs}
        alsoin={pageData.alsoin}
        url=""
        region={''}
        lang={'en'}
      />
      <Box
        as="section"
        className="container-article"
        p="0"
        pt={{ base: '0', md: '64px' }}
        bg="#FFF7E9"
      >
        <MidContainer w={'758'}>
          <Box>
            {imagegallery?.length > 0 ? (
              <MsrImageSlider sectionContent={imagegallery} />
            ) : pageData.types === 'video' ? (
              <Box
                w={{ base: '100%' }}
                mb={{ base: '40px' }}
                pt={{ base: '30px', md: '0px' }}
              >
                <MediaEmbed sectionContent={embedObj} margin="0" />
              </Box>
            ) : (
              <ProgressiveImage
                src={pageData?.cardImage?.url}
                title={pageData?.cardImage?.title}
                alt={pageData?.cardImage?.alt}
                width={{ base: '100%', md: '755px' }}
                height={{ base: '201px', sm: 'auto', lg: '423px' }}
                objectFit="cover"
              />
            )}
          </Box>
          {pageData?.types == 'audio' && pageData?.audioUrl && (
            <Box className="audio-player">
              <Box className="timeline">
                <Box className="progress"></Box>
              </Box>
              <Box className="controls">
                <Box className="play-container">
                  <Box
                    className="toggle-play play"
                    onClick={handleAudioClick}
                  ></Box>
                </Box>
                <Box className="time">
                  <Box className="current"></Box>
                  <Box className="length"></Box>
                </Box>
                <Box className="volume-container">
                  <Box className="volume-button">
                    <Box className="volume icono-volumeMedium"></Box>
                  </Box>
                  <Box className="volume-slider">
                    <Box className="volume-percentage"></Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </MidContainer>
        <MidContainer w={{ base: '90%', md: '592px', lg: '755px' }}>
          <Box
            fontSize={{ base: '36px', lg: '40px' }}
            lineHeight="48px"
            textAlign="center"
            fontFamily="FedraSerifAStdBook"
            mt={{ base: '40px', lg: '60px' }}
            mb="20px"
            as="h1"
            fontWeight="600"
          >
            {pageData.title}
          </Box>
          <Box
            fontFamily="FedraSansStd-book"
            fontSize="16px"
            textAlign="center"
            mb="20px"
          >
            {pageData.summary}
          </Box>
          <Box display="flex" placeContent="center" mb="43px">
            <LazyLoadImageComponent
              src={`${config.imgPath}/d/46272/1668597237-blue-lotus-vector.png`}
            />
          </Box>
        </MidContainer>
        <MidContainer w={'758'}>
          <MidContainer w={{ base: '90%', lg: '755px' }}>
            <Box
              fontFamily="FedraSansStd-book"
              className="structured-content-dark shivablogbody"
              fontSize="16px"
              mb="43px"
            >
              {StructuredTextContent(pageData.body)}
            </Box>
            <SocialIconMob
              data={pageData}
              url={router.asPath}
              isVisible={true}
              direction={'row'}
              position={'static'}
              top="30%"
              left="10%"
              display={{ base: 'flex', md: 'none', xl: 'none' }}
              likesCount={pageData?.likestotalcount}
            />
          </MidContainer>

          {topicsUnAvailable.length < pageData?.topic?.length && (
            <MidContainer w={'768'}>
              <Heading
                as="h4"
                fontSize="26px"
                lineHeight="25px"
                mt="60px"
                ml={{ base: '16px', md: 3, lg: 0 }}
                fontFamily="FedraSansStd-book"
                fontWeight="bold"
              >
                Related Tags
              </Heading>
              <ShivaTopic
                topics={pageData?.topic}
                bgTheme="light"
                page="shiva/topic"
              />
            </MidContainer>
          )}
        </MidContainer>

        <SubscribeForm />
        {pageData?.related_content && (
          <RelatedContent posts={pageData?.related_content} lang={'en'} />
        )}
      </Box>
      <PageConfig
        isLikeVisible={true}
        likesCount={pageData?.likestotalcount}
        shareTextColor="#000000"
        hideInMobile={true}
        pageData={pageData}
      />
      <style global jsx>
        {`
          .structured-content-dark.shivablogbody p {
            color: #000000;
          }
          .structured-content-dark.shivablogbody hr {
            border-top: 1px solid #090d49 !important;
          }
          .structured-content-dark.shivablogbody button a {
            color: #ffffff;
          }
          .structured-content-dark.shivablogbody a {
            color: #090d49;
            font-weight: 500;
            border-bottom: 1px solid #cf4520;
            padding-bottom: 2px;
            cursor: pointer;
            word-break: break-word;
          }
          .structured-content-dark.shivablogbody a:hover {
            background: #cf4520;
            border-bottom: none;
            color: #ffffff !important;
          }
          .structured-content-dark.shivablogbody ol,
          .structured-content-dark.shivablogbody ul {
            padding-left: 1.5em;
          }
          .structured-content-dark.shivablogbody ul li,
          .structured-content-dark.shivablogbody ol li {
            padding: 3px 0.6vw;
          }
          .audioplayer {
            display: flex;
            height: 80px;
            opacity: 0.84;
            background-color: #d8d8d8;
            padding: 0;
          }
          .audio {
            width: 720px;
            height: 80px;
            opacity: 0.9;
            color: #d8d8d8;
          }
          .timeline {
            width: 75%;
            height: 2px;
            margin-top: 38px;
            float: left;
            background: #979797;
            padding: 0;
          }
          .audio-player {
            height: 80px;
            width: 100%;
            background: #202020cc;
            /* box-shadow: 0 0 20px 0 #000a; */
            font-family: arial;
            color: rgb(255 255 255);
            font-size: 0.75em;
            overflow: hidden;
            display: grid;
            grid-template-rows: 6px auto;
            opacity: 0.84;
            margin-top: -86px;
          }
          .audio-player .timeline {
            background: #979797;
            // width: 78%;
            left: 85px;
            position: relative;
            cursor: pointer;
            margin-top: 38px;
            // box-shadow: 0 2px 10px 0 #0008;
          }
          .audio-player .timeline .progress {
            background: #ecb11b;
            width: 0%;
            height: 100%;
            transition: 0.25s;
          }
          .audio-player .timeline .progress:after {
            width: 20px;
            background: red;
            height: 20px;
          }
          .audio-player .controls {
            display: flex;
            justify-content: space-between;
            align-items: stretch;
            padding: 0 20px;
          }
          .audio-player .controls > * {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .audio-player .controls .toggle-play.play {
            cursor: pointer;
            position: relative;
            height: 60px;
            width: 60px;
            border: none;
            background-repeat: no-repeat;
            background-position: center;
            outline: none;
            font-family: 'FedraSansStd-medium';
            margin: 0px 0 0 0;
            background: url(${config.imgPath}/d/46272/1719893899-icon_audio_play.svg)
              no-repeat;
            background-size: 90%;
          }
          .audio-player .controls .toggle-play.play:hover {
            transform: scale(1.1);
          }
          .audio-player .controls .toggle-play.pause {
            position: relative;
            height: 60px;
            width: 60px;
            border: none;
            background-repeat: no-repeat;
            background-position: center;
            outline: none;
            font-family: 'FedraSansStd-medium';
            margin: 0px 0 0 0;
            background: url(${config.imgPath}/d/46272/1663645770-pause.svg)
              no-repeat;
            background-size: 90%;
          }
          .audio-player .controls .time {
            display: flex;
            margin-left: auto;
            color: #333;
            margin-top: -25px;
            font-family: 'FedraSerifAStdBook';
            font-size: 13px;
          }
          .audio-player .controls .time > * {
            padding: 2px;
          }
          .audio-player .controls .volume-container {
            cursor: pointer;
            position: relative;
            z-index: 2;
          }
          .audio-player .controls .volume-container .volume-button {
            height: 26px;
            display: flex;
            align-items: center;
          }
          .audio-player .controls .volume-container .volume-button .volume {
            transform: scale(0.7);
          }
          .audio-player .controls .volume-container .volume-slider {
            position: absolute;
            left: -3px;
            top: 15px;
            z-index: -1;
            width: 0;
            height: 15px;
            background: white;
            box-shadow: 0 0 20px #000a;
            transition: 0.25s;
          }
          .audio-player
            .controls
            .volume-container
            .volume-slider
            .volume-percentage {
            background: coral;
            height: 100%;
            width: 75%;
          }
          .audio-player .controls .volume-container:hover .volume-slider {
            left: -123px;
            width: 120px;
          }
          .toc {
            height: 260px;
            width: 160px;
            display: inline-block;
            padding: 0 0 0.1em 0;
            margin: 0 0.2em 0.2em 0;
          }
          .toc p {
            margin: 0 0 25px 0 !important;
          }
          .toc a {
            border-bottom: none;
          }
          .toc:hover {
            opacity: 0.8;
          }
        `}
      </style>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const msrPaths = config.USE_LOCAL_URL
    ? [
      `/mahashivratri/shiva/shiva-blue-throat/`,
      `/mahashivratri/shiva/shiva-panchakshara-stotram-lyrics`,
      `/mahashivratri/shiva/names-of-shiva-with-meaning/`,
      `/mahashivratri/shiva/guru-purnima-first-guru-born-sadhguru/`,
      `/mahashivratri/shiva/adi-guru/`,
      `/mahashivratri/shiva/embrace-shiva-embrace-life/`,
      `/mahashivratri/shiva/shiva-component-library`,
      `/mahashivratri/shiva/shiva-component-library-1`,
      `/mahashivratri/shiva/shiva-component-library-2`,
      `/mahashivratri/shiva/shiva-component-library-3`,
    ]
    : await getLandingPaths('L1', '/mahashivratri/shiva/', 'shiva_section', [
      '/mahashivratri/shiva/power-of-the-mantra',
      '/mahashivratri/topic/',
      '/mahashivratri/shiva/story-of-nayanmars',
    ]);
  return {
    paths: msrPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({ preview, previewData, params: { blog } }) => {
  const headerMenuData = await fetchHeaderMenuData(
    getLocale('IN', 'en'),
    'msrmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale('IN', 'en'),
    'msrfooter'
  );
  const pageData = await fetchDetails('', '', `shiva/${blog}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      menuType: 'msrmainmenu',
    },
  };
};

export default ShivaBlog;
